/**
 * Catalog → Bulk Import.
 *
 * Drives the reusable import engine: upload a workbook, choose run options,
 * dry-run it, then import for real — with a live progress bar, pause/resume,
 * a per-row error log, retry-failed, and the history of every previous run.
 *
 * The page never parses or validates the file itself. Everything the operator
 * sees comes from the same server-side pipeline that does the writing, so the
 * preview cannot disagree with the outcome.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  FileUp,
  History,
  Info,
  Layers,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  Trash2,
  Undo2,
  Upload,
  X,
  XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import type { productImport as pi } from '@terra/contracts';
import { Badge, Spinner, toast } from '@terra/ui';

import { api } from '../lib/api';

type Mode = pi.ImportJobMode;

const ACTIVE_STATUSES: pi.ImportJobStatus[] = ['QUEUED', 'VALIDATING', 'RUNNING', 'PAUSING'];
const isActive = (s: pi.ImportJobStatus) => ACTIVE_STATUSES.includes(s);

const STATUS_TONE: Record<pi.ImportJobStatus, 'neutral' | 'success' | 'warning' | 'danger'> = {
  QUEUED: 'neutral',
  VALIDATING: 'neutral',
  RUNNING: 'neutral',
  PAUSING: 'warning',
  PAUSED: 'warning',
  COMPLETED: 'success',
  FAILED: 'danger',
  CANCELLED: 'neutral',
  UNDOING: 'warning',
  UNDONE: 'neutral',
};

const ROW_TONE: Record<pi.ImportRowStatus, 'neutral' | 'success' | 'warning' | 'danger'> = {
  PENDING: 'neutral',
  RUNNING: 'neutral',
  CREATED: 'success',
  UPDATED: 'success',
  SKIPPED: 'warning',
  FAILED: 'danger',
};

export function BulkImportProductsPage() {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [showMappings, setShowMappings] = useState(false);
  const [rowFilter, setRowFilter] = useState<pi.ImportRowStatus | 'ALL'>('ALL');

  const defaults = useQuery({
    queryKey: ['import-defaults'],
    queryFn: () => api.productImport.getDefaults(),
  });

  const [options, setOptions] = useState<Partial<pi.ImportOptions>>({});
  const [mappings, setMappings] = useState<pi.VendorMappingRule[]>([]);

  // Seed the form from the server's declared defaults the first time they land.
  useEffect(() => {
    if (!defaults.data) return;
    setOptions((prev) => (Object.keys(prev).length > 0 ? prev : defaults.data.options));
    setMappings((prev) => (prev.length > 0 ? prev : defaults.data.vendorMappings));
  }, [defaults.data]);

  // ── Active job ───────────────────────────────────────────────────────────
  const job = useQuery({
    queryKey: ['import-job', jobId],
    queryFn: () => api.productImport.getJob(jobId!),
    enabled: Boolean(jobId),
    // Poll while the run is moving; stop the moment it settles.
    refetchInterval: (query) =>
      query.state.data && isActive(query.state.data.status) ? 1500 : false,
  });

  const rows = useQuery({
    queryKey: ['import-rows', jobId, rowFilter],
    queryFn: () =>
      api.productImport.listRows(jobId!, {
        pageSize: 200,
        ...(rowFilter === 'ALL' ? {} : { status: rowFilter }),
      }),
    enabled: Boolean(jobId),
    refetchInterval: job.data && isActive(job.data.status) ? 3000 : false,
  });

  const history = useQuery({
    queryKey: ['import-jobs'],
    queryFn: () => api.productImport.listJobs({ pageSize: 20 }),
    refetchInterval: job.data && isActive(job.data.status) ? 5000 : false,
  });

  const refreshAll = () => {
    void qc.invalidateQueries({ queryKey: ['import-job', jobId] });
    void qc.invalidateQueries({ queryKey: ['import-rows', jobId] });
    void qc.invalidateQueries({ queryKey: ['import-jobs'] });
    void qc.invalidateQueries({ queryKey: ['catalog-products'] });
  };

  /** One mutation shape for every job-lifecycle button. */
  const lifecycle = useMutation({
    mutationFn: async ({
      action,
    }: {
      action: 'pause' | 'resume' | 'cancel' | 'retryFailed' | 'undo';
    }) => {
      await api.productImport[action](jobId!);
    },
    onSuccess: (_result, { action }) => {
      toast.success(
        {
          pause: 'Pausing after the current row…',
          resume: 'Resuming import',
          cancel: 'Import cancelled',
          retryFailed: 'Failed rows queued for retry',
          undo: 'Import rolled back',
        }[action],
      );
      refreshAll();
    },
    onError: (e: unknown) => toast.error(errorText(e)),
  });
  const running = (action: string) => lifecycle.isPending && lifecycle.variables?.action === action;

  const start = useMutation({
    mutationFn: async (mode: Mode) => {
      if (!file) throw new Error('Choose a file first');
      setUploading(true);
      try {
        const sig = await api.files.getUploadSignature({ folder: 'imports' });
        const form = new FormData();
        form.append('file', file);
        form.append('api_key', sig.apiKey);
        form.append('timestamp', String(sig.timestamp));
        form.append('signature', sig.signature);
        form.append('folder', sig.folder);

        const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloudName}/raw/upload`, {
          method: 'POST',
          body: form,
        });
        if (!res.ok) throw new Error('Could not upload the file to storage');
        const uploaded = (await res.json()) as { secure_url?: string };
        if (!uploaded.secure_url) throw new Error('Storage did not return a file URL');

        return api.productImport.createJob({
          sourceName: file.name,
          sourceUrl: uploaded.secure_url,
          mode,
          options: { ...options, vendorMappings: mappings },
        });
      } finally {
        setUploading(false);
      }
    },
    onSuccess: (created) => {
      setJobId(created.id);
      toast.success(created.mode === 'DRY_RUN' ? 'Dry run started' : 'Import started');
      refreshAll();
    },
    onError: (e: unknown) => toast.error(errorText(e)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.productImport.deleteJob(id),
    onSuccess: (_r, id) => {
      if (id === jobId) setJobId(null);
      toast.success('Run deleted');
      refreshAll();
    },
    onError: (e: unknown) => toast.error(errorText(e)),
  });

  const current = job.data ?? null;
  const progress =
    current && current.totalRows > 0
      ? Math.round((current.processedRows / current.totalRows) * 100)
      : 0;

  const preIssues = current?.preValidation?.issues ?? [];
  const errors = preIssues.filter((i) => i.severity === 'ERROR');
  const warnings = preIssues.filter((i) => i.severity === 'WARNING');
  const failedCount = current?.failedCount ?? 0;

  const setOption = <K extends keyof pi.ImportOptions>(key: K, value: pi.ImportOptions[K]) =>
    setOptions((prev) => ({ ...prev, [key]: value }));

  const onPickFile = (picked: File | undefined) => {
    if (!picked) return;
    if (!/\.(xlsx|csv)$/i.test(picked.name)) {
      toast.error('Choose an .xlsx or .csv file');
      return;
    }
    setFile(picked);
    setJobId(null);
  };

  return (
    <div className="pb-8">
      <div className="mb-6 flex items-center justify-between">
        <div className="text-ink-subtle flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider">
          <Link to="/products/all" className="hover:text-gold-700 transition-colors">
            Catalog
          </Link>
          <span className="mx-1">/</span>
          <span className="text-ink">Bulk Import</span>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* ── Left: setup ─────────────────────────────────────────────── */}
        <div className="space-y-6 lg:col-span-1">
          <Panel icon={<Layers className="h-4 w-4" strokeWidth={2} />} title="Import Setup">
            <div className="space-y-5 p-5">
              <Field label="Source file" required>
                <div
                  className={`border-border/60 bg-surface-subtle/30 hover:bg-surface-subtle/50 flex h-28 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed transition-colors ${dragActive ? 'border-ink bg-surface-subtle/50' : ''}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragActive(true);
                  }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragActive(false);
                    onPickFile(e.dataTransfer.files?.[0]);
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="text-ink-muted h-5 w-5" />
                  <span className="text-ink-subtle text-[12px] font-medium">
                    {file ? file.name : 'Drop an .xlsx or .csv here, or click'}
                  </span>
                  <input
                    type="file"
                    accept=".xlsx,.csv"
                    className="hidden"
                    ref={fileInputRef}
                    onChange={(e) => onPickFile(e.target.files?.[0])}
                  />
                </div>
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Default HSN/SAC" hint="Used for rows with no HSN column">
                  <TextInput
                    value={options.defaultHsnSac ?? ''}
                    onChange={(v) => setOption('defaultHsnSac', v)}
                    placeholder="e.g. 2106"
                  />
                </Field>
                <Field label="Default GST %">
                  <TextInput
                    value={String(options.defaultGstRate ?? 18)}
                    onChange={(v) => setOption('defaultGstRate', Number(v) || 0)}
                  />
                </Field>
              </div>

              <Field label="New products land as">
                <SelectInput
                  value={options.productStatus ?? 'APPROVED'}
                  onChange={(v) =>
                    setOption('productStatus', v as pi.ImportOptions['productStatus'])
                  }
                  options={[
                    ['APPROVED', 'Approved — live immediately'],
                    ['PENDING_APPROVAL', 'Pending approval'],
                    ['DRAFT', 'Draft'],
                  ]}
                />
              </Field>

              <Field
                label="Fallback category"
                hint="Only used for products the source site lists under no category at all. Leave empty and those rows fail instead of being filed on a guess."
              >
                <TextInput
                  value={options.fallbackCategoryPath ?? ''}
                  onChange={(v) => setOption('fallbackCategoryPath', v)}
                  placeholder="e.g. Uncategorised"
                />
              </Field>

              <Field
                label="Local image folder"
                hint="Repo-relative; checked before downloading anything"
              >
                <TextInput
                  value={options.localImageDir ?? ''}
                  onChange={(v) => setOption('localImageDir', v)}
                  placeholder="apps/storefront/public/images/images-product"
                />
              </Field>

              <div className="space-y-2.5">
                <Toggle
                  label="Fill gaps from the Product URL"
                  hint="Vendor, category, description, images and SEO are read from the live page when the file is missing or has corrupted them"
                  checked={options.enrichFromProductUrl ?? true}
                  onChange={(v) => setOption('enrichFromProductUrl', v)}
                />
                <Toggle
                  label="Recover categories from the source site"
                  hint="Crawls the storefront's category listings to rebuild each product's full hierarchy"
                  checked={options.enrichCategoriesFromSite ?? true}
                  onChange={(v) => setOption('enrichCategoriesFromSite', v)}
                />
                <Toggle
                  label="Import images"
                  hint="Downloads every image and uploads it to your storage"
                  checked={options.importImages ?? true}
                  onChange={(v) => setOption('importImages', v)}
                />
              </div>

              <Field label="Vendor preference when sources disagree">
                <SelectInput
                  value={options.vendorSourcePreference ?? 'PRODUCT_PAGE'}
                  onChange={(v) =>
                    setOption('vendorSourcePreference', v as 'PRODUCT_PAGE' | 'SHEET')
                  }
                  options={[
                    ['PRODUCT_PAGE', 'Prefer the product page'],
                    ['SHEET', 'Prefer the sheet'],
                  ]}
                />
              </Field>
            </div>

            <div className="border-border/60 bg-surface-subtle/30 space-y-2 border-t p-5">
              <button
                onClick={() => start.mutate('DRY_RUN')}
                disabled={!file || uploading || start.isPending}
                className="border-border/60 text-ink hover:bg-surface-subtle flex w-full items-center justify-center gap-2 rounded-xl border px-5 py-2.5 text-[13px] font-bold transition-colors disabled:opacity-50"
              >
                {uploading || start.isPending ? (
                  <Spinner size="sm" />
                ) : (
                  <FileUp className="h-4 w-4" />
                )}
                Dry run (write nothing)
              </button>
              <button
                onClick={() => start.mutate('LIVE')}
                disabled={!file || uploading || start.isPending}
                className="bg-ink hover:bg-ink/90 flex w-full items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
              >
                <Play className="h-4 w-4" />
                Import for real
              </button>
            </div>
          </Panel>

          {/* Vendor mapping editor */}
          <Panel
            icon={<Info className="h-4 w-4" strokeWidth={2} />}
            title="Brand → Vendor mapping"
            action={
              <button
                onClick={() => setShowMappings((v) => !v)}
                className="text-ink-subtle hover:text-ink text-[12px] font-bold"
              >
                {showMappings ? 'Hide' : `Show (${mappings.length})`}
              </button>
            }
          >
            {showMappings && (
              <div className="space-y-2 p-5">
                <p className="text-ink-subtle text-[12px]">
                  These rules decide the vendor before any other signal. A brand with no rule falls
                  back to the sheet, then the product page — never to a guess.
                </p>
                {mappings.map((rule, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <TextInput
                      value={rule.brand}
                      onChange={(v) =>
                        setMappings((prev) =>
                          prev.map((r, j) => (i === j ? { ...r, brand: v } : r)),
                        )
                      }
                      placeholder="Brand"
                    />
                    <span className="text-ink-subtle text-[12px]">→</span>
                    <TextInput
                      value={rule.vendorStoreName}
                      onChange={(v) =>
                        setMappings((prev) =>
                          prev.map((r, j) => (i === j ? { ...r, vendorStoreName: v } : r)),
                        )
                      }
                      placeholder="Vendor store"
                    />
                    <label
                      className="text-ink-subtle flex shrink-0 items-center gap-1 text-[11px]"
                      title="Create this store if it does not exist"
                    >
                      <input
                        type="checkbox"
                        checked={rule.createIfMissing}
                        onChange={(e) =>
                          setMappings((prev) =>
                            prev.map((r, j) =>
                              i === j ? { ...r, createIfMissing: e.target.checked } : r,
                            ),
                          )
                        }
                      />
                      new
                    </label>
                    <button
                      onClick={() => setMappings((prev) => prev.filter((_, j) => j !== i))}
                      className="text-ink-subtle rounded p-1 hover:bg-red-50 hover:text-red-500"
                      title="Remove rule"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() =>
                    setMappings((prev) => [
                      ...prev,
                      { brand: '', vendorStoreName: '', createIfMissing: false },
                    ])
                  }
                  className="text-ink-subtle hover:text-ink text-[12px] font-bold"
                >
                  + Add rule
                </button>

                <div className="border-border/60 mt-4 border-t pt-4">
                  <p className="text-ink mb-1 text-[13px] font-semibold">Vendor name aliases</p>
                  <p className="text-ink-subtle mb-2 text-[12px]">
                    Use when the source prints a different name for a store you already have
                    (&ldquo;Yogiraj Food Products&rdquo; → &ldquo;Yogiraj Foods&rdquo;). Names are
                    never matched approximately — an alias has to say so.
                  </p>
                  {(options.vendorAliases ?? []).map((alias, i) => (
                    <div key={i} className="mb-2 flex items-center gap-2">
                      <TextInput
                        value={alias.sourceName}
                        onChange={(v) =>
                          setOption(
                            'vendorAliases',
                            (options.vendorAliases ?? []).map((a, j) =>
                              i === j ? { ...a, sourceName: v } : a,
                            ),
                          )
                        }
                        placeholder="Name in the source"
                      />
                      <span className="text-ink-subtle text-[12px]">→</span>
                      <TextInput
                        value={alias.vendorStoreName}
                        onChange={(v) =>
                          setOption(
                            'vendorAliases',
                            (options.vendorAliases ?? []).map((a, j) =>
                              i === j ? { ...a, vendorStoreName: v } : a,
                            ),
                          )
                        }
                        placeholder="Existing store"
                      />
                      <button
                        onClick={() =>
                          setOption(
                            'vendorAliases',
                            (options.vendorAliases ?? []).filter((_, j) => j !== i),
                          )
                        }
                        className="text-ink-subtle rounded p-1 hover:bg-red-50 hover:text-red-500"
                        title="Remove alias"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() =>
                      setOption('vendorAliases', [
                        ...(options.vendorAliases ?? []),
                        { sourceName: '', vendorStoreName: '' },
                      ])
                    }
                    className="text-ink-subtle hover:text-ink text-[12px] font-bold"
                  >
                    + Add alias
                  </button>
                </div>
              </div>
            )}
          </Panel>
        </div>

        {/* ── Right: run state ────────────────────────────────────────── */}
        <div className="space-y-6 lg:col-span-2">
          {!current ? (
            <Panel icon={<FileUp className="h-4 w-4" strokeWidth={2} />} title="No active run">
              <div className="flex flex-col items-center justify-center p-12 text-center">
                <div className="bg-surface-subtle text-ink-muted ring-border/60 mb-4 grid h-12 w-12 place-items-center rounded-xl ring-1 ring-inset">
                  <FileUp className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <h3 className="text-ink text-[15px] font-bold">Nothing running</h3>
                <p className="text-ink-subtle mt-1.5 max-w-md text-[13px]">
                  Upload a workbook and start with a dry run. It exercises the full pipeline —
                  enrichment, vendor and category resolution, image fetching, validation — without
                  writing anything, then tells you exactly what a real import would do.
                </p>
              </div>
            </Panel>
          ) : (
            <>
              <Panel
                icon={
                  isActive(current.status) ? (
                    <RefreshCw className="h-4 w-4 animate-spin" strokeWidth={2} />
                  ) : current.status === 'COMPLETED' ? (
                    <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
                  ) : (
                    <AlertCircle className="h-4 w-4" strokeWidth={2} />
                  )
                }
                title={`${current.sourceName} — ${current.mode === 'DRY_RUN' ? 'Dry run' : 'Live import'}`}
                action={<Badge tone={STATUS_TONE[current.status]}>{current.status}</Badge>}
              >
                <div className="space-y-5 p-5">
                  <div>
                    <div className="text-ink-subtle mb-1.5 flex justify-between text-[12px] font-medium">
                      <span>
                        {current.processedRows} of {current.totalRows} rows
                      </span>
                      <span>{progress}%</span>
                    </div>
                    <div className="bg-surface-subtle h-2 w-full overflow-hidden rounded-full">
                      <div
                        className="bg-ink h-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
                    <Stat label="Created" value={current.createdCount} />
                    <Stat label="Updated" value={current.updatedCount} />
                    <Stat label="Failed" value={current.failedCount} danger={failedCount > 0} />
                    <Stat label="Vendors +" value={current.vendorsCreated} />
                    <Stat label="Categories +" value={current.categoriesCreated} />
                    <Stat label="Brands +" value={current.brandsCreated} />
                    <Stat label="Images ↓" value={current.imagesDownloaded} />
                    <Stat label="Images ↑" value={current.imagesUploaded} />
                    <Stat label="Skipped" value={current.skippedCount} />
                  </div>

                  {current.error && (
                    <div className="rounded-xl border border-red-100 bg-red-50 p-3 text-[12px] text-red-700">
                      {current.error}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    {isActive(current.status) && (
                      <ActionButton
                        onClick={() => lifecycle.mutate({ action: 'pause' })}
                        pending={running('pause')}
                        icon={<Pause className="h-3.5 w-3.5" />}
                        label="Pause"
                      />
                    )}
                    {(current.status === 'PAUSED' ||
                      current.status === 'FAILED' ||
                      current.status === 'CANCELLED') && (
                      <ActionButton
                        onClick={() => lifecycle.mutate({ action: 'resume' })}
                        pending={running('resume')}
                        icon={<Play className="h-3.5 w-3.5" />}
                        label="Resume"
                      />
                    )}
                    {failedCount > 0 && !isActive(current.status) && (
                      <ActionButton
                        onClick={() => lifecycle.mutate({ action: 'retryFailed' })}
                        pending={running('retryFailed')}
                        icon={<RotateCcw className="h-3.5 w-3.5" />}
                        label={`Retry ${failedCount} failed row${failedCount === 1 ? '' : 's'}`}
                      />
                    )}
                    {isActive(current.status) && (
                      <ActionButton
                        onClick={() => lifecycle.mutate({ action: 'cancel' })}
                        pending={running('cancel')}
                        icon={<XCircle className="h-3.5 w-3.5" />}
                        label="Cancel"
                      />
                    )}
                    {current.mode === 'LIVE' &&
                      !isActive(current.status) &&
                      current.status !== 'UNDONE' &&
                      current.createdCount + current.updatedCount > 0 && (
                        <ActionButton
                          onClick={() => {
                            const n = current.createdCount + current.updatedCount;
                            if (
                              window.confirm(
                                `Roll back this import?

` +
                                  `${current.createdCount} product(s) created by it will be removed and ` +
                                  `${current.updatedCount} updated product(s) will be restored to how they were before.

` +
                                  `Vendors, categories and brands it created are kept — other products may now use them.

` +
                                  `This affects ${n} product(s).`,
                              )
                            ) {
                              lifecycle.mutate({ action: 'undo' });
                            }
                          }}
                          pending={running('undo')}
                          icon={<Undo2 className="h-3.5 w-3.5" />}
                          label="Undo import"
                        />
                      )}
                  </div>
                </div>
              </Panel>

              {/* Validation */}
              {(errors.length > 0 || warnings.length > 0) && (
                <Panel
                  icon={<AlertTriangle className="h-4 w-4" strokeWidth={2} />}
                  title={`Pre-import validation — ${errors.length} error${errors.length === 1 ? '' : 's'}, ${warnings.length} warning${warnings.length === 1 ? '' : 's'}`}
                >
                  <ul className="max-h-64 space-y-1.5 overflow-y-auto p-5 text-[12px]">
                    {[...errors, ...warnings].map((i, n) => (
                      <li key={n} className="flex gap-2">
                        <span
                          className={i.severity === 'ERROR' ? 'text-red-500' : 'text-amber-500'}
                        >
                          {i.severity === 'ERROR' ? '✗' : '!'}
                        </span>
                        <span className="text-ink-subtle">
                          {i.row !== null && <span className="font-mono">row {i.row}: </span>}
                          {i.message}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Panel>
              )}

              {current.postValidation && (
                <Panel
                  icon={
                    current.postValidation.passed ? (
                      <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
                    ) : (
                      <XCircle className="h-4 w-4" strokeWidth={2} />
                    )
                  }
                  title="Post-import validation"
                  action={
                    <Badge tone={current.postValidation.passed ? 'success' : 'danger'}>
                      {current.postValidation.passed ? 'PASSED' : 'FAILED'}
                    </Badge>
                  }
                >
                  <ul className="space-y-1.5 p-5 text-[12px]">
                    {current.postValidation.checks.map((c, n) => (
                      <li key={n} className="flex gap-2">
                        <span className={c.passed ? 'text-green-600' : 'text-red-500'}>
                          {c.passed ? '✓' : '✗'}
                        </span>
                        <span className="text-ink font-semibold">{c.name}</span>
                        <span className="text-ink-subtle">— {c.detail}</span>
                      </li>
                    ))}
                  </ul>
                </Panel>
              )}

              {current.undoReport && (
                <Panel
                  icon={<Undo2 className="h-4 w-4" strokeWidth={2} />}
                  title="Rollback report"
                  action={
                    <Badge tone={current.undoReport.failed > 0 ? 'danger' : 'success'}>
                      {current.undoReport.failed > 0 ? 'PARTIAL' : 'COMPLETE'}
                    </Badge>
                  }
                >
                  <div className="space-y-3 p-5 text-[12px]">
                    <div className="grid grid-cols-4 gap-3">
                      <Stat label="Removed" value={current.undoReport.deleted} />
                      <Stat label="Restored" value={current.undoReport.restored} />
                      <Stat label="Skipped" value={current.undoReport.skipped} />
                      <Stat
                        label="Failed"
                        value={current.undoReport.failed}
                        danger={current.undoReport.failed > 0}
                      />
                    </div>
                    <p className="text-ink-subtle">
                      Kept in place: {current.undoReport.notRolledBack.join(', ')}.{' '}
                      {current.undoReport.note}
                    </p>
                    {current.undoReport.problems.length > 0 && (
                      <ul className="list-disc space-y-0.5 pl-4 text-red-600">
                        {current.undoReport.problems.map((p, i) => (
                          <li key={i}>{p}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Panel>
              )}

              {/* Row log */}
              <Panel
                icon={<Layers className="h-4 w-4" strokeWidth={2} />}
                title="Row log"
                action={
                  <select
                    className="border-border/60 bg-surface text-ink rounded-lg border px-2 py-1 text-[12px] font-semibold"
                    value={rowFilter}
                    onChange={(e) => setRowFilter(e.target.value as typeof rowFilter)}
                  >
                    <option value="ALL">All rows</option>
                    <option value="FAILED">Failed</option>
                    <option value="CREATED">Created</option>
                    <option value="UPDATED">Updated</option>
                    <option value="PENDING">Pending</option>
                  </select>
                }
              >
                <div className="max-h-[520px] overflow-auto">
                  {rows.isLoading ? (
                    <div className="flex justify-center p-8">
                      <Spinner size="md" />
                    </div>
                  ) : (rows.data?.data.length ?? 0) === 0 ? (
                    <p className="text-ink-subtle p-8 text-center text-[13px]">No rows to show.</p>
                  ) : (
                    <table className="w-full text-left text-[12px]">
                      <thead className="bg-surface-subtle text-ink-subtle sticky top-0 z-10 font-mono uppercase">
                        <tr>
                          <th className="border-border/60 w-14 border-b px-3 py-2 font-bold">#</th>
                          <th className="border-border/60 w-24 border-b px-3 py-2 font-bold">
                            Status
                          </th>
                          <th className="border-border/60 border-b px-3 py-2 font-bold">Product</th>
                          <th className="border-border/60 border-b px-3 py-2 font-bold">
                            Resolved / message
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-border/40 divide-y">
                        {rows.data!.data.map((row) => (
                          <RowLine key={row.id} row={row} />
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </Panel>
            </>
          )}

          {/* History */}
          <Panel icon={<History className="h-4 w-4" strokeWidth={2} />} title="Import history">
            <div className="max-h-80 overflow-auto">
              {history.isLoading ? (
                <div className="flex justify-center p-8">
                  <Spinner size="md" />
                </div>
              ) : (history.data?.data.length ?? 0) === 0 ? (
                <p className="text-ink-subtle p-8 text-center text-[13px]">No imports run yet.</p>
              ) : (
                <table className="w-full text-left text-[12px]">
                  <thead className="bg-surface-subtle text-ink-subtle sticky top-0 z-10 font-mono uppercase">
                    <tr>
                      <th className="border-border/60 border-b px-3 py-2 font-bold">File</th>
                      <th className="border-border/60 border-b px-3 py-2 font-bold">Mode</th>
                      <th className="border-border/60 border-b px-3 py-2 font-bold">Status</th>
                      <th className="border-border/60 border-b px-3 py-2 font-bold">Result</th>
                      <th className="border-border/60 border-b px-3 py-2 font-bold">When</th>
                      <th className="border-border/60 w-20 border-b px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-border/40 divide-y">
                    {history.data!.data.map((h) => (
                      <tr
                        key={h.id}
                        className={`hover:bg-surface-subtle/30 cursor-pointer transition-colors ${h.id === jobId ? 'bg-surface-subtle/40' : ''}`}
                        onClick={() => setJobId(h.id)}
                      >
                        <td className="text-ink px-3 py-2 font-semibold">{h.sourceName}</td>
                        <td className="text-ink-subtle px-3 py-2">
                          {h.mode === 'DRY_RUN' ? 'Dry run' : 'Live'}
                        </td>
                        <td className="px-3 py-2">
                          <Badge tone={STATUS_TONE[h.status]}>{h.status}</Badge>
                        </td>
                        <td className="text-ink-subtle px-3 py-2">
                          {h.createdCount} created · {h.updatedCount} updated ·{' '}
                          <span className={h.failedCount > 0 ? 'font-bold text-red-500' : ''}>
                            {h.failedCount} failed
                          </span>
                        </td>
                        <td className="text-ink-subtle px-3 py-2">
                          {new Date(h.createdAt).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              remove.mutate(h.id);
                            }}
                            className="text-ink-subtle rounded p-1 hover:bg-red-50 hover:text-red-500"
                            title="Delete this run from history"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

// ── Row line ─────────────────────────────────────────────────────────────────

function RowLine({ row }: { row: pi.ImportRow }) {
  const [open, setOpen] = useState(false);
  const resolved = row.resolved as {
    vendor?: string;
    vendorSource?: string;
    categoryPath?: string[];
    slug?: string;
  } | null;
  const title = useMemo(() => {
    const raw = row.raw;
    const named = Object.entries(raw).find(([k]) => /product name|title/i.test(k));
    return (named?.[1] as string | undefined) ?? resolved?.slug ?? row.sourceKey ?? '—';
  }, [row, resolved]);

  return (
    <>
      <tr
        className="hover:bg-surface-subtle/30 cursor-pointer transition-colors"
        onClick={() => setOpen((v) => !v)}
      >
        <td className="text-ink-subtle px-3 py-2 font-mono">{row.rowNumber}</td>
        <td className="px-3 py-2">
          <Badge tone={ROW_TONE[row.status]}>{row.status}</Badge>
        </td>
        <td className="text-ink px-3 py-2 font-semibold">
          <span className="inline-flex items-center gap-1">
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            {title}
          </span>
        </td>
        <td className="text-ink-subtle px-3 py-2">
          {row.error ? (
            <span className="text-red-600">{row.error}</span>
          ) : resolved ? (
            <>
              {resolved.vendor}
              {resolved.categoryPath?.length ? ` · ${resolved.categoryPath.join(' > ')}` : ''}
            </>
          ) : (
            '—'
          )}
        </td>
      </tr>
      {open && (
        <tr className="bg-surface-subtle/20">
          <td colSpan={4} className="px-6 py-3">
            {row.messages.length > 0 && (
              <ul className="text-ink-subtle mb-2 list-disc space-y-0.5 pl-4 text-[11px]">
                {row.messages.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            )}
            <pre className="text-ink-subtle max-h-56 overflow-auto whitespace-pre-wrap break-all text-[11px]">
              {JSON.stringify({ resolved: row.resolved, source: row.raw }, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Small presentational pieces ──────────────────────────────────────────────

function Panel({
  icon,
  title,
  action,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  action?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
      <div className="border-border/60 bg-surface-subtle/30 flex items-center gap-3 border-b px-5 py-4">
        <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 shrink-0 place-items-center rounded-lg ring-1 ring-inset">
          {icon}
        </div>
        <h3 className="text-ink flex-1 truncate text-[14px] font-bold">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-ink text-[13px] font-semibold">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {hint && <span className="text-ink-subtle text-[11px]">{hint}</span>}
    </div>
  );
}

const inputClass =
  'border-border/60 bg-surface-subtle/50 text-ink focus:border-ink focus:ring-ink/20 focus:bg-surface h-10 w-full rounded-xl border px-3 text-[13px] font-medium outline-none transition-colors focus:ring-2';

function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      className={inputClass}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function SelectInput({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<[string, string]>;
}) {
  return (
    <select className={inputClass} value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map(([v, label]) => (
        <option key={v} value={v}>
          {label}
        </option>
      ))}
    </select>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5">
      <input
        type="checkbox"
        className="mt-0.5"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="text-ink block text-[13px] font-semibold">{label}</span>
        {hint && <span className="text-ink-subtle block text-[11px]">{hint}</span>}
      </span>
    </label>
  );
}

function Stat({ label, value, danger }: { label: string; value: number; danger?: boolean }) {
  return (
    <div className="border-border/60 rounded-xl border p-2.5 text-center">
      <p className="text-ink-subtle text-[10px] font-bold uppercase tracking-wider">{label}</p>
      <p className={`text-lg font-bold ${danger ? 'text-red-500' : 'text-ink'}`}>{value}</p>
    </div>
  );
}

function ActionButton({
  onClick,
  pending,
  icon,
  label,
}: {
  onClick: () => void;
  pending: boolean;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={pending}
      className="border-border/60 text-ink hover:bg-surface-subtle flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[12px] font-bold transition-colors disabled:opacity-50"
    >
      {pending ? <Spinner size="sm" /> : icon}
      {label}
    </button>
  );
}

function errorText(e: unknown): string {
  if (e && typeof e === 'object' && 'message' in e) return String((e as Error).message);
  return 'Something went wrong';
}
